import { useCallback, useEffect, useState } from 'react';
import type { BridgeStatus } from './useOftBridgeStatus';
import { LZ_SCAN_API_URL } from '../config/oft';

export interface PendingBridge {
  /** Source chain transaction hash */
  srcTxHash: string;
  /** EVM chain ID of the source chain */
  srcChainId: number;
  /** Amount sent in local decimals (6 for USDT0) */
  amount: bigint;
  /** Unix timestamp (ms) when the bridge was initiated */
  timestamp: number;
  /** Current bridge status */
  status: BridgeStatus;
}

/** localStorage-serializable form (bigint → string) */
interface SerializedPendingBridge {
  srcTxHash: string;
  srcChainId: number;
  amount: string;
  timestamp: number;
  status: BridgeStatus;
}

function serializeBridge(bridge: PendingBridge): SerializedPendingBridge {
  return { ...bridge, amount: bridge.amount.toString() };
}

function deserializeBridge(bridge: SerializedPendingBridge): PendingBridge {
  return { ...bridge, amount: BigInt(bridge.amount) };
}

function getStorageKey(address: `0x${string}`): string {
  return `pendingBridges:${address.toLowerCase()}`;
}

function loadBridgesFromStorage(
  address: `0x${string}`,
): PendingBridge[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(getStorageKey(address));
    if (!raw) return [];
    const parsed: SerializedPendingBridge[] = JSON.parse(raw);
    return parsed.map(deserializeBridge);
  } catch {
    return [];
  }
}

function saveBridgesToStorage(
  address: `0x${string}`,
  bridges: PendingBridge[],
): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(
      getStorageKey(address),
      JSON.stringify(bridges.map(serializeBridge)),
    );
  } catch {
    // localStorage may be unavailable in some environments
  }
}

async function fetchBridgeStatus(srcTxHash: string): Promise<BridgeStatus> {
  try {
    const response = await fetch(`${LZ_SCAN_API_URL}/${srcTxHash}`);
    if (!response.ok) return 'pending';

    const data = await response.json();
    if (!data.messages || data.messages.length === 0) return 'pending';

    const statusName: string = data.messages[0].status.name.toLowerCase();
    if (statusName === 'delivered' || statusName === 'success') return 'delivered';
    if (statusName === 'failed' || statusName === 'blocked') return 'failed';
    if (statusName === 'inflight' || statusName === 'confirming') return 'inflight';
    return 'pending';
  } catch {
    return 'pending';
  }
}

/**
 * Manages pending bridge transactions stored in localStorage.
 *
 * On mount, checks the status of all non-completed bridges via the LZ Scan API.
 * Exposes helpers to add new bridges and update statuses.
 *
 * @param address - The user's wallet address (used as localStorage namespace key)
 */
export function usePendingBridges(address: `0x${string}` | undefined) {
  const [pendingBridges, setPendingBridges] = useState<PendingBridge[]>([]);

  // Load from localStorage on mount / address change
  useEffect(() => {
    if (!address) {
      setPendingBridges([]);
      return;
    }
    const loaded = loadBridgesFromStorage(address);
    setPendingBridges(loaded);
  }, [address]);

  // On mount (or when address changes), refresh statuses of non-delivered bridges
  useEffect(() => {
    if (!address) return;

    const bridges = loadBridgesFromStorage(address);
    const uncompleted = bridges.filter(
      (b) => b.status !== 'delivered' && b.status !== 'failed',
    );

    if (uncompleted.length === 0) return;

    // Fire-and-forget status refresh on mount
    Promise.allSettled(
      uncompleted.map(async (bridge) => {
        const newStatus = await fetchBridgeStatus(bridge.srcTxHash);
        return { srcTxHash: bridge.srcTxHash, newStatus };
      }),
    ).then((results) => {
      if (!address) return;

      // Read fresh from storage to avoid stale closure
      const current = loadBridgesFromStorage(address);
      let changed = false;

      const updated = current.map((bridge) => {
        const result = results.find(
          (r) =>
            r.status === 'fulfilled' &&
            r.value.srcTxHash === bridge.srcTxHash,
        );
        if (result && result.status === 'fulfilled') {
          const { newStatus } = result.value;
          if (newStatus !== bridge.status) {
            changed = true;
            return { ...bridge, status: newStatus };
          }
        }
        return bridge;
      });

      if (changed) {
        saveBridgesToStorage(address, updated);
        setPendingBridges(updated);
      } else {
        setPendingBridges(current);
      }
    });
  }, [address]);

  const addPendingBridge = useCallback(
    (bridge: Omit<PendingBridge, 'status'>) => {
      if (!address) return;
      const newBridge: PendingBridge = { ...bridge, status: 'pending' };
      setPendingBridges((prev) => {
        const updated = [...prev, newBridge];
        saveBridgesToStorage(address, updated);
        return updated;
      });
    },
    [address],
  );

  const updateBridgeStatus = useCallback(
    (srcTxHash: string, status: BridgeStatus) => {
      if (!address) return;
      setPendingBridges((prev) => {
        const updated = prev.map((b) =>
          b.srcTxHash === srcTxHash ? { ...b, status } : b,
        );
        saveBridgesToStorage(address, updated);
        return updated;
      });
    },
    [address],
  );

  /** Bridges that are not yet delivered or failed */
  const uncompletedBridges = pendingBridges.filter(
    (b) => b.status !== 'delivered' && b.status !== 'failed',
  );

  return {
    pendingBridges,
    uncompletedBridges,
    addPendingBridge,
    updateBridgeStatus,
  };
}
